import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaExclamationTriangle, FaHome, FaArrowLeft } from 'react-icons/fa';

/**
 * NotFound Component
 * A premium, modern 404 error page with glassmorphism and animations.
 */
export default function NotFound() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-950 via-indigo-900 to-purple-950 flex items-center justify-center px-4 font-sans overflow-hidden relative">
            {/* Animated background elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
                <div className="absolute top-[10%] left-[5%] w-72 h-72 bg-blue-600/20 rounded-full blur-[100px] animate-pulse"></div>
                <div className="absolute bottom-[10%] right-[5%] w-96 h-96 bg-purple-600/20 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>
                <div className="absolute top-[40%] right-[15%] w-64 h-64 bg-indigo-500/10 rounded-full blur-[80px] animate-blob"></div>

                {/* Decorative lines (if animate-line exists in index.css) */}
                <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
                    <div className="absolute top-1/4 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-blue-500 to-transparent animate-line"></div>
                    <div className="absolute top-3/4 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-purple-500 to-transparent animate-line animation-delay-2000"></div>
                </div>
            </div>

            <div className="max-w-lg w-full bg-white/10 backdrop-blur-xl border border-white/20 p-8 md:p-12 rounded-[2.5rem] shadow-2xl relative z-10 text-center animate-fade-in">
                <div className="mb-8 relative flex justify-center">
                    <div className="w-32 h-32 bg-red-500/20 rounded-full flex items-center justify-center relative">
                        <div className="absolute inset-0 bg-red-500/20 rounded-full animate-ping"></div>
                        <FaExclamationTriangle className="text-6xl text-red-400 relative z-10" />
                    </div>
                </div>

                <h1 className="text-7xl font-black text-white mb-2 tracking-tighter drop-shadow-lg">404</h1>
                <h2 className="text-2xl font-bold text-blue-200 mb-4 uppercase tracking-widest">Page Not Found</h2>

                <p className="text-blue-100/70 mb-10 leading-relaxed text-lg">
                    Oops! The page you're looking for seems to have vanished into the digital void or you don't have permission to see it.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center justify-center gap-2 px-6 py-4 bg-white/5 border border-white/10 text-white rounded-2xl font-bold hover:bg-white/10 transition-all active:scale-95 group"
                    >
                        <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
                        Go Back
                    </button>

                    <button
                        onClick={() => navigate('/')}
                        className="flex items-center justify-center gap-2 px-6 py-4 bg-white text-blue-950 rounded-2xl font-bold hover:bg-blue-50 transition-all shadow-xl shadow-blue-900/20 active:scale-95 group"
                    >
                        <FaHome className="group-hover:scale-110 transition-transform" />
                        Home Page
                    </button>
                </div>

                <p className="mt-12 text-sm text-white/30 font-medium">
                    &copy; {new Date().getFullYear()} Sudesh Maths. All rights reserved.
                </p>
            </div>
        </div>
    );
}
